/**
 * §16.1 wizard frame: 8-step stepper, draft autosave with conflict handling, per-step validation summary that
 * links each issue to its exact field (client preview via `validateLotteryConfig`; the server is authoritative),
 * a persistent summary rail (D-2606) and a "preview as participant" link. Steps render through nested routes
 * and read the config slice from react-hook-form context (`useFormContext<DraftConfigInput>()`).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { totalLiabilityMinor, type LotteryAdminDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import { Callout } from '../../../components/Callout';
import { Card } from '../../../components/Card';
import { ErrorState } from '../../../components/EmptyState';
import { FormErrorSummary } from '../../../components/FormField';
import { PageHeader } from '../../../components/PageHeader';
import { DetailSkeleton } from '../../../components/PageSkeleton';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../../components/StatusBadge';
import { lotteriesApi } from '../../../lib/api/admin';
import { isApiError } from '../../../lib/api/client';
import { readRuntimeConfig } from '../../../lib/runtime-config';
import {
  DraftConfigSchema,
  WIZARD_STEPS,
  newDraftConfig,
  previewIssues,
  stepForPath,
  stepIndex,
  toDraftInput,
  type DraftConfigInput,
  type PreviewIssue,
  type WizardStepId,
} from './steps';
import { useDraftAutosave, type DraftAutosave } from './useDraftAutosave';

export interface WizardContext {
  lottery: LotteryAdminDto | null;
  lotteryId: string | null;
  issues: PreviewIssue[];
  autosave: DraftAutosave;
  changeNote: string;
  setChangeNote: (v: string) => void;
  participantRemedy: string;
  setParticipantRemedy: (v: string) => void;
  goToStep: (step: WizardStepId) => void;
}

export const useWizard = (): WizardContext => useOutletContext<WizardContext>();

const stepPath = (lotteryId: string | null, step: WizardStepId): string =>
  lotteryId ? `/lotteries/${lotteryId}/edit/${step}` : `/lotteries/new/${step}`;

export function WizardFrame() {
  const { id, step: stepParam } = useParams();
  const lotteryId = id ?? null;
  const query = useQuery({
    queryKey: ['lottery', lotteryId],
    queryFn: () => lotteriesApi.get(lotteryId ?? ''),
    enabled: Boolean(lotteryId),
  });

  if (lotteryId && query.isPending) return <DetailSkeleton label="Loading draft" />;
  if (lotteryId && (query.isError || !query.data)) {
    return (
      <ErrorState
        title="Draft could not be loaded"
        body="It may belong to another tenant or have been removed."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button href="/lotteries">Back to lotteries</Button>}
      />
    );
  }
  return (
    <WizardForm
      key={lotteryId ?? 'new'}
      lottery={query.data ?? null}
      lotteryId={lotteryId}
      step={(stepParam as WizardStepId | undefined) ?? 'template'}
    />
  );
}

function WizardForm({
  lottery,
  lotteryId: initialId,
  step,
}: {
  lottery: LotteryAdminDto | null;
  lotteryId: string | null;
  step: WizardStepId;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { webOrigin } = readRuntimeConfig();
  const [lotteryId, setLotteryId] = useState(initialId);
  const [changeNote, setChangeNote] = useState('');
  const [participantRemedy, setParticipantRemedy] = useState('');

  const defaults = useMemo<DraftConfigInput>(
    () => (lottery ? toDraftInput(lottery.config) : (newDraftConfig() as DraftConfigInput)),
    [lottery],
  );
  const form = useForm<DraftConfigInput>({
    defaultValues: defaults,
    resolver: zodResolver(DraftConfigSchema) as never,
    mode: 'onBlur',
  });
  const values = useWatch({ control: form.control });

  const issues = useMemo(
    () => previewIssues(values, lottery?.configVersion ?? 1),
    [values, lottery?.configVersion],
  );
  const serverIssues: PreviewIssue[] = useMemo(
    () =>
      (lottery?.validation.issues ?? []).map((i) => ({
        ...i,
        path: i.path.replace(/\[(\d+)\]/g, '.$1'),
        step: stepForPath(i.path),
      })),
    [lottery],
  );

  const onCreated = useCallback(
    (created: LotteryAdminDto) => {
      setLotteryId(created.lotteryId);
      navigate(`/lotteries/${created.lotteryId}/edit/${step}`, { replace: true });
    },
    [navigate, step],
  );

  const autosave = useDraftAutosave({
    form,
    lotteryId,
    initialVersion: lottery?.configVersion ?? null,
    hasEntries: lottery?.hasEntries ?? false,
    changeNote,
    participantRemedy,
    onCreated,
    enabled: !lottery || ['DRAFT', 'REVIEW', 'APPROVED'].includes(lottery.status),
  });

  const current = stepIndex(step) === -1 ? 0 : stepIndex(step);
  const stepDef = WIZARD_STEPS[current] ?? WIZARD_STEPS[0]!;
  const goToStep = useCallback((s: WizardStepId) => navigate(stepPath(lotteryId, s)), [navigate, lotteryId]);
  const stepIssues = issues.filter((i) => i.step === stepDef.id);
  const errorCount = issues.filter((i) => i.severity === 'error').length;
  const countByStep = (s: WizardStepId) =>
    issues.filter((i) => i.step === s && i.severity === 'error').length;
  const slug = (values.identity?.slug as string | undefined) ?? '';
  const title = (values.presentation?.content as Record<string, { title?: string }> | undefined)?.[
    (values.identity?.locales as string[] | undefined)?.[0] ?? 'en'
  ]?.title;

  const context: WizardContext = {
    lottery,
    lotteryId,
    issues,
    autosave,
    changeNote,
    setChangeNote,
    participantRemedy,
    setParticipantRemedy,
    goToStep,
  };
  const previewHref = slug ? `${webOrigin}/l/${slug}?preview=1` : undefined;

  const rail = (
    <SummaryRail
      values={values}
      lottery={lottery}
      issues={issues}
      intl={intl}
      goToStep={goToStep}
      autosave={autosave}
    />
  );

  return (
    <FormProvider {...form}>
      <PageHeader
        breadcrumb={[
          { label: 'Lotteries', to: '/lotteries' },
          { label: lottery ? title || lottery.slug : 'New lottery' },
          { label: stepDef.label },
        ]}
        kicker={`Step ${current + 1} of ${WIZARD_STEPS.length} · ${stepDef.label}`}
        title={lottery ? title || lottery.slug : 'Create a lottery'}
        meta={lottery ? <StatusBadge kind="LotteryStatus" value={lottery.status} /> : null}
        description={stepDef.description}
        actions={
          <>
            <Button
              variant="secondary"
              href={previewHref}
              target="_blank"
              rel="noreferrer"
              disabled={!previewHref}
              disabledReason={!previewHref ? 'Set a slug first.' : undefined}
            >
              Preview as participant
            </Button>
            <Button
              variant="secondary"
              onClick={() => void autosave.saveNow()}
              loading={autosave.status.kind === 'saving'}
            >
              Save draft
            </Button>
          </>
        }
      />
      <div className="wdc-wizard">
        <nav aria-label="Wizard steps">
          <ol className="wdc-stepper">
            {WIZARD_STEPS.map((s, i) => {
              const n = countByStep(s.id);
              return (
                <li key={s.id}>
                  <NavLink
                    to={stepPath(lotteryId, s.id)}
                    aria-current={s.id === stepDef.id ? 'step' : undefined}
                    data-complete={n === 0 && i < current ? 'true' : undefined}
                    data-issues={n > 0 ? 'true' : undefined}
                    data-testid={`step-${s.id}`}
                  >
                    <span>{s.label}</span>
                    {n > 0 ? (
                      <span
                        className="wdc-stepper__count wdc-badge"
                        data-tone="error"
                        data-size="sm"
                        aria-label={`${n} issue${n === 1 ? '' : 's'}`}
                      >
                        {n}
                      </span>
                    ) : null}
                  </NavLink>
                </li>
              );
            })}
          </ol>
          <p className="wdc-stepper__meta">
            Step {current + 1} of {WIZARD_STEPS.length} · {errorCount} blocking issue
            {errorCount === 1 ? '' : 's'}
          </p>
        </nav>
        <div className="wdc-wizard__panel">
          <SaveStatusLine autosave={autosave} intl={intl} />
          {lottery?.hasEntries ? (
            <Callout tone="warning" title="Entries exist.">
              <p>
                Material changes create a new configuration version and need a change note and participant
                remedy (Review step) before they save (§5.4).
              </p>
            </Callout>
          ) : null}
          {stepDef.id !== 'review' ? (
            <FormErrorSummary
              issues={stepIssues}
              title={`${stepIssues.length} issue${stepIssues.length === 1 ? '' : 's'} on this step`}
              onNavigate={(path) => goToStep(stepForPath(path))}
            />
          ) : null}
          {serverIssues.length && stepDef.id !== 'review' ? (
            <details>
              <summary className="wd-muted">Server validation from last save ({serverIssues.length})</summary>
              <FormErrorSummary
                issues={serverIssues.filter((i) => i.step === stepDef.id)}
                title="Reported by the API"
                onNavigate={(path) => goToStep(stepForPath(path))}
              />
            </details>
          ) : null}
          <details className="wdc-wizard__rail-toggle">
            <summary>Draft summary</summary>
            {rail}
          </details>
          <Card>
            <Outlet context={context} />
          </Card>
          <div className="wdc-wizard__nav">
            {current > 0 ? (
              <Link
                className="wd-btn"
                data-variant="secondary"
                to={stepPath(lotteryId, WIZARD_STEPS[current - 1]!.id)}
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Back: {WIZARD_STEPS[current - 1]!.label}
              </Link>
            ) : (
              <span />
            )}
            {current < WIZARD_STEPS.length - 1 ? (
              <Link
                className="wd-btn"
                data-variant="primary"
                to={stepPath(lotteryId, WIZARD_STEPS[current + 1]!.id)}
              >
                Next: {WIZARD_STEPS[current + 1]!.label}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            ) : null}
          </div>
        </div>
        <aside className="wdc-wizard__rail" aria-label="Draft summary">
          {rail}
        </aside>
      </div>
    </FormProvider>
  );
}

/** Persistent draft summary (D-2606): the facts an operator checks before publishing, always in view. */
function SummaryRail({
  values,
  lottery,
  issues,
  intl,
  goToStep,
  autosave,
}: {
  values: unknown;
  lottery: LotteryAdminDto | null;
  issues: PreviewIssue[];
  intl: ReturnType<typeof useIntl>;
  goToStep: (s: WizardStepId) => void;
  autosave: DraftAutosave;
}) {
  const parsed = useMemo(() => DraftConfigSchema.safeParse(values), [values]);
  const v = (values ?? {}) as Record<string, unknown>;
  const identity = v.identity as { slug?: string; currency?: string; timezone?: string } | undefined;
  const outcome = v.outcome as { mechanic?: string } | undefined;
  const entry = v.entry as { mode?: string; priceMinor?: string } | undefined;
  const schedule = v.schedule as { opensAt?: string; closesAt?: string; drawAt?: string } | undefined;
  const prizes = (v.prizes as { prizes?: unknown[] } | undefined)?.prizes ?? [];
  const sponsors = (v.sponsors as { sponsors?: unknown[] } | undefined)?.sponsors ?? [];
  const currency = identity?.currency ?? 'XTS';
  const liability = parsed.success ? totalLiabilityMinor(parsed.data) : null;
  const errors = issues.filter((i) => i.severity === 'error');
  const byStep = WIZARD_STEPS.map((s) => ({
    step: s,
    count: errors.filter((i) => i.step === s.id).length,
  })).filter((x) => x.count > 0);
  const fmt = (iso?: string) => (iso ? intl.formatDateTime(iso) : '—');
  return (
    <>
      <Card title="Draft summary" as="h2">
        <dl className="wdc-facts">
          <div>
            <dt>Slug</dt>
            <dd>
              {identity?.slug ? <code>{identity.slug}</code> : <span className="wd-muted">not set</span>}
            </dd>
          </div>
          <div>
            <dt>Mechanic</dt>
            <dd>{outcome?.mechanic ? humanizeStatus(outcome.mechanic) : '—'}</dd>
          </div>
          <div>
            <dt>Entry</dt>
            <dd>
              {entry?.mode === 'PAID'
                ? `Paid · ${intl.formatMoney(String(entry.priceMinor ?? '0'), currency)}`
                : 'Free'}
            </dd>
          </div>
          <div>
            <dt>Opens</dt>
            <dd>{fmt(schedule?.opensAt)}</dd>
          </div>
          <div>
            <dt>Closes</dt>
            <dd>{fmt(schedule?.closesAt)}</dd>
          </div>
          {schedule?.drawAt ? (
            <div>
              <dt>Draw</dt>
              <dd>{fmt(schedule.drawAt)}</dd>
            </div>
          ) : null}
          <div>
            <dt>Prizes</dt>
            <dd>
              {prizes.length}
              {liability !== null ? ` · liability ${intl.formatMoney(liability.toString(), currency)}` : ''}
            </dd>
          </div>
          <div>
            <dt>Sponsors</dt>
            <dd>{sponsors.length}</dd>
          </div>
          <div>
            <dt>Timezone</dt>
            <dd>{identity?.timezone ?? '—'}</dd>
          </div>
          <div>
            <dt>Version</dt>
            <dd>
              {lottery ? `v${lottery.configVersion}` : 'unsaved'}
              {autosave.status.kind === 'saved' ? ` · saved ${intl.formatTime(autosave.status.at)}` : ''}
            </dd>
          </div>
        </dl>
      </Card>
      <Card
        title="Readiness"
        as="h2"
        tone={errors.length === 0 ? 'success' : 'error'}
        description={
          errors.length === 0
            ? 'No blocking issues in the client preview.'
            : `${errors.length} blocking issue${errors.length === 1 ? '' : 's'} across ${byStep.length} step${byStep.length === 1 ? '' : 's'}.`
        }
      >
        {errors.length === 0 ? (
          <ToneBadge tone="success">Ready for review</ToneBadge>
        ) : (
          <ul className="wdc-wizard__issues">
            {byStep.map(({ step, count }) => (
              <li key={step.id}>
                <a
                  href={`#step-${step.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goToStep(step.id);
                  }}
                >
                  <span>{step.label}</span>
                  <ToneBadge tone="error">{count}</ToneBadge>
                </a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function SaveStatusLine({ autosave, intl }: { autosave: DraftAutosave; intl: ReturnType<typeof useIntl> }) {
  const s = autosave.status;
  return (
    <div className="wdc-wizard__status" role="status" data-testid="save-status">
      {s.kind === 'idle' ? <span data-state="idle">Draft autosaves as you edit.</span> : null}
      {s.kind === 'dirty' ? <span data-state="dirty">Unsaved changes…</span> : null}
      {s.kind === 'saving' ? <span data-state="saving">Saving…</span> : null}
      {s.kind === 'saved' ? (
        <span data-state="saved">
          Saved v{s.version} at {intl.formatTime(s.at)}.
        </span>
      ) : null}
      {s.kind === 'invalid' ? (
        <span data-state="invalid" className="text-warning">
          {s.message}
        </span>
      ) : null}
      {s.kind === 'error' ? (
        <span data-state="error" className="text-error">
          {s.message}
        </span>
      ) : null}
      {s.kind === 'conflict' ? (
        <>
          <span data-state="conflict" className="text-error">
            Someone else saved a newer version. {s.message}
          </span>
          <Button variant="secondary" onClick={() => void autosave.reloadServerCopy()}>
            Load their version
          </Button>
          <Button variant="danger" onClick={() => void autosave.overwriteServerCopy()}>
            Overwrite with mine
          </Button>
        </>
      ) : null}
    </div>
  );
}
